/**
 * useDriverStateV2 - React Query Enhanced Driver State Hook
 *
 * This hook replaces the original useDriverState with React Query for automatic data synchronization.
 * Key features:
 *
 * - **Automatic Refetching**: Calls are refetched every 30 seconds for real-time updates
 * - **Background Sync**: Data continues to sync even when the app is in the background
 * - **Window Focus Sync**: Data is refreshed when the app regains focus
 * - **Error Handling**: Built-in retry logic and error states
 * - **Optimistic Updates**: Immediate UI updates with background sync
 * - **No Infinite Loops**: Uses refs for stable function references
 *
 * Real-time behavior:
 * - Profile data: Refetches every 24 hours (stale time: 1 hour)
 * - Call data: Refetches every 30 seconds (stale time: 30 seconds)
 * - Call details: Manual refresh only (stale time: 60 seconds)
 * - Vehicle notes: Refetches every 2 minutes (stale time: 30 seconds)
 * - Location updates: Throttled to every 30 seconds
 * - Manual refresh: Available via actions.refreshData()
 *
 * This ensures the CallScreen and other components automatically receive updated
 * dispatch/call data when changes are made in the admin dashboard.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useHybridAuth } from './useHybridAuth';
import {
  Call,
  Task,
  Driver,
  DriverStatus,
  mapDriverTaskToTask,
} from '../types';
import { useDriverProfile } from './useDriverProfile';
import { useDriverCalls } from './useDriverCalls';
import { useDriverTasks } from './useDriverTasks';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';
import { useUpdateLocation } from './useUpdateLocation';
import { DriverProfile, DriverCall, driverApi } from '../services/driverApi';
import { shouldHideDispatch } from '../utils/dispatchFilter';
import { isValidDriverRecordId } from '../utils/driverRecordId';
import { AuthErrorKind, authErrorKindFromError } from '../utils/authErrorKind';

interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
}

interface LocationError {
  code: number;
  message: string;
}

interface DriverState {
  driver: Driver | null;
  calls: Call[];
  tasks: Task[];
  location: LocationCoordinates | null;
  locationError: LocationError | null;
  isLocationLoading: boolean;
  isDataLoading: boolean;
  dataError: string | null;
  authError: AuthErrorKind | null;
  sessionError: AuthErrorKind | null;
  fallbackMode: boolean;
}

// Mapping functions to convert API types to component types
const mapDriverProfileToDriver = (
  profile: DriverProfile,
  _calls: DriverCall[] = []
): Driver => {
  // Use the status directly from the profile instead of deriving it from calls
  // This ensures we're using the actual database status value
  let driverStatus: DriverStatus =
    (profile.status as DriverStatus) || 'unknown';

  return {
    id: profile.id,
    userId: profile.userId,
    firstName: profile.name.split(' ')[0] || '',
    lastName: profile.name.split(' ').slice(1).join(' ') || '',
    email: profile.email,
    phone: profile.phone,
    licenseNumber: profile.employeeNumber,
    vehicleInfo: {
      make: '',
      model: '',
      year: 0,
      licensePlate: profile.truckNumber,
      color: '',
      towingCapacity: 0,
    },
    status: driverStatus,
    location: {
      latitude: profile.locationLatitude,
      longitude: profile.locationLongitude,
    },
    createdAt: profile.startDate,
    updatedAt: new Date().toISOString(),
    companyId: companyIdFromProfileOrStorage(profile),
  };
};

const mapDriverCallToCall = (driverCall: DriverCall): Call => {
  // Map API status to component status based on dispatch status
  let status: Call['status'] = 'waiting';
  if (driverCall.status === 'NEW') {
    status = 'ready'; // NEW calls are ready to be started
  } else if (driverCall.status === 'PENDING') {
    status = 'waiting'; // PENDING calls are waiting
  } else if (driverCall.status === 'DISPATCHED') {
    status = 'en_route'; // DISPATCHED calls are en route
  } else if (driverCall.status === 'ON_SCENE') {
    status = 'on_scene'; // ON_SCENE calls are on scene
  } else if (driverCall.status === 'TOWING') {
    status = 'towing'; // TOWING calls are towing
  } else if (driverCall.status === 'COMPLETED') {
    status = 'complete'; // COMPLETED calls are complete
  } else if (
    driverCall.status === 'FAILED' ||
    driverCall.status === 'CANCELED'
  ) {
    status = 'canceled'; // CANCELED/FAILED calls map to canceled
  }

  // Map API priority to component priority
  let priority: Call['priority'] = 'normal';
  if (driverCall.priority === 'LOW') priority = 'low';
  else if (driverCall.priority === 'NORMAL') priority = 'normal';
  else if (driverCall.priority === 'HIGH') priority = 'high';
  else if (driverCall.priority === 'URGENT') priority = 'emergency';

  return {
    id: driverCall.id,
    address: driverCall.pickupLocation,
    dispatchedTime: driverCall.createdAt,
    status,
    accountInfo: driverCall.account,
    priority,
    dispatchNumber: driverCall.dispatchNumber,
    type: driverCall.type,
    contactName: driverCall.contactName,
    contactPhone: driverCall.contactPhone,
    pickupLocationLat: 0, // Not provided by API
    pickupLocationLong: 0, // Not provided by API
    destinationLat: 0, // Not provided by API
    destinationLong: 0, // Not provided by API
    scheduledAt: driverCall.scheduledAt,
    completionTime: null,
    mileage: null,
    totalCharges: null,
    notes: driverCall.notes,
    billingNotes: null,
    includeBillingNotes: false,
    account: driverCall.account,
    vehicle: driverCall.vehicle,
    source: driverCall.source,
    sourceMetadata: driverCall.sourceMetadata,
    pickupLocation: {
      address: driverCall.pickupLocation,
      latitude: 0, // Not provided by API
      longitude: 0, // Not provided by API
    },
    dropoffLocation: {
      address: driverCall.destination,
      latitude: 0, // Not provided by API
      longitude: 0, // Not provided by API
    },
    vehicleInfo: {
      make: driverCall.vehicle.vehicleName || 'Unknown',
      model: driverCall.vehicle.vehicleModel || 'Unknown',
      year: driverCall.vehicle.vehicleYear || 0,
      licensePlate: driverCall.vehicle.licensePlate,
      color: driverCall.vehicle.vehicleAttributes?.color || '', // Not provided by VIN decode API
      vin: driverCall.vehicle.vin,
    },
    vehicleId: driverCall.vehicle.id,
    description: driverCall.notes || undefined,
    createdAt: driverCall.createdAt,
    startedAt: driverCall.startedAt,
    arrivedAt: driverCall.arrivedAt,
  };
};

// Sort calls by status in the specified order: RETURNING, ONSITE, DISPATCHED, NEW, PENDING
const sortCallsByStatus = (calls: Call[]): Call[] => {
  const statusOrder: Record<Call['status'], number> = {
    towing: 0, // TOWING status maps to towing (highest priority)
    on_scene: 1, // ONSITE status maps to on_scene
    en_route: 2, // DISPATCHED status maps to en_route
    ready: 3, // NEW status maps to ready
    waiting: 4, // PENDING status maps to waiting
    complete: 5, // COMPLETED status maps to complete
    canceled: 6,
  };

  return calls.sort((a, b) => {
    const aOrder = statusOrder[a.status] ?? 999;
    const bOrder = statusOrder[b.status] ?? 999;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    // If same status, sort by priority (emergency first, then high, normal, low)
    const priorityOrder = { emergency: 0, high: 1, normal: 2, low: 3 };
    const aPriority = priorityOrder[a.priority || 'normal'];
    const bPriority = priorityOrder[b.priority || 'normal'];

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same status and priority, sort by creation time (oldest first)
    return (
      new Date(a.dispatchedTime).getTime() -
      new Date(b.dispatchedTime).getTime()
    );
  });
};

export const useDriverStateV2 = () => {
  const { isAuthenticated, getAccessTokenSilently } = useHybridAuth();
  const [locationState, setLocationState] = useState({
    location: null as LocationCoordinates | null,
    locationError: null as LocationError | null,
    isLocationLoading: false,
  });
  const [optimisticTasks, setOptimisticTasks] = useState<Task[]>([]);
  const locationWatchId = useRef<number | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars

  // React Query hooks
  const {
    data: profileData,
    error: profileError,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useDriverProfile();

  // Backend returns placeholder profile { id: '0' } when no Driver row exists for user+company; the board would be empty then — do not ask until we have a real driver id.
  const fetchCallsEnabled =
    isAuthenticated &&
    !isProfileLoading &&
    isValidDriverRecordId(profileData?.id);

  // Every driver-mobile request needs company context. Take it from the
  // profile, which arrives before anything else, rather than from the stored
  // selection: that is written only after the companies list loads, and on a
  // fresh login the first calls fetch used to race it and fail with a 400.
  const companyIdForApi = (() => {
    const id = companyIdFromProfileOrStorage(profileData);
    return id || undefined;
  })();

  const {
    data: callsData,
    error: callsError,
    isLoading: isCallsLoading,
    refetch: refetchCalls,
  } = useDriverCalls(undefined, {
    queryEnabled: fetchCallsEnabled,
    companyId: companyIdForApi,
  });

  const {
    data: tasksData,
    error: tasksError,
    isLoading: isTasksLoading,
    refetch: refetchTasks,
  } = useDriverTasks(
    profileData?.id || '', // Pass driver ID when available
    companyIdForApi // Align with axios interceptor + multi-company selection
  );

  // Debug logging for tasks
  useEffect(() => {
    if (tasksData) {
      console.log('📋 [useDriverStateV2] Tasks data received:', {
        isArray: Array.isArray(tasksData),
        length: Array.isArray(tasksData) ? tasksData.length : 'not array',
        data: tasksData,
      });
    }
  }, [tasksData]);

  const updateLocationMutation = useUpdateLocation(companyIdForApi);

  // Store the mutation function in a ref to avoid dependency issues
  const updateLocationRef = useRef(updateLocationMutation.mutate);
  updateLocationRef.current = updateLocationMutation.mutate;

  // Combine loading states
  const isDataLoading = isProfileLoading || isCallsLoading || isTasksLoading;

  // Create detailed error message with API call name
  const dataError = useMemo(() => {
    if (profileError?.message) {
      return profileError.message;
    }
    if (callsError?.message) {
      return callsError.message;
    }
    if (tasksError?.message) {
      return tasksError.message;
    }
    return null;
  }, [profileError?.message, callsError?.message, tasksError?.message]);

  // Which auth failure this is, straight off the status the API attached.
  // A 401 is an expired token and a 403 is a missing company link; the UI has
  // to be able to tell them apart or a screenshot tells us nothing.
  const authError = useMemo(
    () =>
      authErrorKindFromError(profileError) ||
      authErrorKindFromError(callsError) ||
      authErrorKindFromError(tasksError),
    [profileError, callsError, tasksError]
  );

  // The same question, asked of the profile request alone, for the callers that
  // want to blank the whole screen. A 401 or 403 on the profile means the
  // session itself is dead. A 403 on calls or tasks can be one route the driver
  // is not allowed to see, and taking the app away for that would strand a
  // driver who is mid job.
  const sessionError = useMemo(
    () => authErrorKindFromError(profileError),
    [profileError]
  );

  // Transform data
  const driver = useMemo(() => {
    if (!profileData) return null;
    return mapDriverProfileToDriver(profileData, callsData?.data || []);
  }, [profileData, callsData]);

  const calls = useMemo(() => {
    if (!callsData?.data) return [];
    // Exclude ACCEPTED_AWAITING (not actionable by drivers)
    const filtered = callsData.data.filter(
      (c) => !shouldHideDispatch(c.status)
    );
    return sortCallsByStatus(filtered.map(mapDriverCallToCall));
  }, [callsData]);

  const tasks = useMemo(() => {
    // Use optimistic tasks if available, otherwise use server data
    if (optimisticTasks.length > 0) {
      return optimisticTasks;
    }
    if (!tasksData || !Array.isArray(tasksData)) return [];
    return tasksData.map(mapDriverTaskToTask);
  }, [tasksData, optimisticTasks]);

  // Location tracking functions
  const requestLocation = useCallback(() => {
    // Check if we're in embedded mode first
    const isEmbedded =
      new URLSearchParams(window.location.search).get('embedded') === 'true';

    if (isEmbedded) {
      // In embedded mode, request location from native app
      if (
        typeof (window as any).ReactNativeWebView?.getCurrentLocation ===
        'function'
      ) {
        console.log('📍 Requesting location from native app');
        setLocationState((prev) => ({
          ...prev,
          isLocationLoading: true,
          locationError: null,
        }));
        (window as any).ReactNativeWebView.getCurrentLocation();
        return; // Don't use browser geolocation
      }
    }

    // Only use browser geolocation if not in embedded mode
    if (!navigator.geolocation) {
      setLocationState((prev) => ({
        ...prev,
        locationError: {
          code: -1,
          message: 'Geolocation is not supported by this browser',
        },
        isLocationLoading: false,
      }));
      return;
    }

    setLocationState((prev) => ({
      ...prev,
      isLocationLoading: true,
      locationError: null,
    }));

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 300000, // 5 minutes - use cached location if available
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const newLocation = {
          latitude,
          longitude,
          accuracy: accuracy || undefined,
          timestamp: Date.now(),
        };

        setLocationState((prev) => ({
          ...prev,
          location: newLocation,
          locationError: null,
          isLocationLoading: false,
        }));

        // Send location to backend using React Query mutation
        try {
          updateLocationRef.current({ latitude, longitude });
          console.log(
            `📍 [WebView] Location updated: lat=${latitude.toFixed(6)}, lng=${longitude.toFixed(6)}, accuracy=${accuracy?.toFixed(1)}m`
          );
        } catch (error) {
          console.error('Failed to update location on server:', error);
        }
      },
      (error) => {
        let errorMessage = 'Unknown location error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        setLocationState((prev) => ({
          ...prev,
          locationError: {
            code: error.code,
            message: errorMessage,
          },
          isLocationLoading: false,
        }));
      },
      options
    );
  }, []); // Remove updateLocationMutation dependency

  const startLocationTracking = useCallback(() => {
    // Check if we're in embedded mode first
    const isEmbedded =
      new URLSearchParams(window.location.search).get('embedded') === 'true';

    if (isEmbedded) {
      // In embedded mode, native app handles location tracking
      console.log(
        '📍 Location tracking handled by native app in embedded mode'
      );
      return null; // Don't use browser geolocation
    }

    if (!navigator.geolocation) {
      setLocationState((prev) => ({
        ...prev,
        locationError: {
          code: -1,
          message: 'Geolocation is not supported by this browser',
        },
      }));
      return null;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout to 15 seconds
      maximumAge: 120000, // Increased to 2 minutes for continuous tracking to reduce frequency
    };

    let lastUpdateTime = 0;
    const UPDATE_THROTTLE = 30000; // Only send updates every 30 seconds minimum

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const now = Date.now();

        const newLocation = {
          latitude,
          longitude,
          accuracy: accuracy || undefined,
          timestamp: now,
        };

        setLocationState((prev) => ({
          ...prev,
          location: newLocation,
          locationError: null,
          isLocationLoading: false,
        }));

        // Throttle backend updates to prevent flooding
        if (now - lastUpdateTime >= UPDATE_THROTTLE) {
          try {
            updateLocationRef.current({ latitude, longitude });
            lastUpdateTime = now;
            console.log(
              `📍 [WebView] Location tracked and sent to server: lat=${latitude.toFixed(6)}, lng=${longitude.toFixed(6)}, accuracy=${accuracy?.toFixed(1)}m`
            );
          } catch (error) {
            console.error('Failed to update location on server:', error);
          }
        } else {
          console.log(
            `📍 [WebView] Location tracked (throttled, not sent to server): lat=${latitude.toFixed(6)}, lng=${longitude.toFixed(6)}, accuracy=${accuracy?.toFixed(1)}m`
          );
        }
      },
      (error) => {
        let errorMessage = 'Unknown location error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        setLocationState((prev) => ({
          ...prev,
          locationError: {
            code: error.code,
            message: errorMessage,
          },
          isLocationLoading: false,
        }));
      },
      options
    );

    return watchId;
  }, []); // Remove updateLocationMutation dependency

  const stopLocationTracking = useCallback((watchId: number) => {
    if (navigator.geolocation && watchId) {
      navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  // Store refetch functions in refs to avoid dependency issues
  const refetchProfileRef = useRef(refetchProfile);
  const refetchCallsRef = useRef(refetchCalls);
  const refetchTasksRef = useRef(refetchTasks);
  refetchProfileRef.current = refetchProfile;
  refetchCallsRef.current = refetchCalls;
  refetchTasksRef.current = refetchTasks;

  // Call status actions (these would need backend mutations in a real implementation)
  const updateCallStatus = useCallback(
    (callId: string, status: Call['status']) => {
      // TODO: Implement backend mutation and then refetch calls
      console.log('Update call status:', callId, status);
      // For now, just trigger a refetch
      refetchCallsRef.current();
    },
    []
  );

  const completeTask = useCallback(
    async (taskId: string) => {
      const companyId = companyIdFromProfileOrStorage(profileData);
      if (!driver?.id || !companyId) {
        console.error('Cannot complete task: missing driver ID or company ID');
        return;
      }

      try {
        console.log('Completing task:', taskId);

        // Get current tasks from server data
        const currentTasks = tasksData
          ? tasksData.map(mapDriverTaskToTask)
          : [];

        // Optimistically update the task status in the UI
        const updatedTasks = currentTasks.map((task) =>
          task.id === taskId
            ? { ...task, status: 'completed' as const, completedAt: new Date() }
            : task
        );

        // Set optimistic state immediately for instant UI feedback
        setOptimisticTasks(updatedTasks);

        // Call the API to complete the task
        const token = await getAccessTokenSilently();
        driverApi.setAuthToken(token);

        await driverApi.completeTask(taskId, companyId);

        // Clear optimistic state and refetch to get the updated status from server
        setOptimisticTasks([]);
        refetchTasksRef.current();

        console.log('Task completed successfully');
      } catch (error) {
        console.error('Failed to complete task:', error);
        // If the API call fails, clear optimistic state to show server state
        setOptimisticTasks([]);
      }
    },
    [driver?.id, profileData, getAccessTokenSilently, tasksData]
  );

  const updateDriverStatus = useCallback((status: DriverStatus) => {
    // TODO: Implement backend mutation and then refetch profile
    console.log('Update driver status:', status);
    // For now, just trigger a refetch
    refetchProfileRef.current();
  }, []);

  const refreshData = useCallback(() => {
    refetchProfileRef.current();
    refetchCallsRef.current();
    refetchTasksRef.current();
  }, []);

  // Helper functions to set location state directly (for Android interface)
  const setLocation = useCallback((location: LocationCoordinates) => {
    setLocationState((prev) => ({
      ...prev,
      location,
      locationError: null,
      isLocationLoading: false,
    }));
  }, []);

  const setLocationError = useCallback((error: LocationError) => {
    setLocationState((prev) => ({
      ...prev,
      locationError: error,
      isLocationLoading: false,
    }));
  }, []);

  const actions = useMemo(
    () => ({
      updateCallStatus,
      completeTask,
      updateDriverStatus,
      requestLocation,
      startLocationTracking,
      stopLocationTracking,
      refreshData,
      setLocation,
      setLocationError,
    }),
    [
      updateCallStatus,
      completeTask,
      updateDriverStatus,
      requestLocation,
      startLocationTracking,
      stopLocationTracking,
      refreshData,
      setLocation,
      setLocationError,
    ]
  );

  const state: DriverState = {
    driver,
    calls,
    tasks,
    location: locationState.location,
    locationError: locationState.locationError,
    isLocationLoading: locationState.isLocationLoading,
    isDataLoading,
    dataError,
    authError,
    sessionError,
    fallbackMode: dataError !== null && driver === null,
  };

  return {
    ...state,
    actions,
  };
};
